import React from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { isValidInternalPath } from "../utils/navigation";
import Footer from "./Footer";

const AuthContainer = styled.div<{ $isOrganization?: boolean }>`
	min-height: 100vh;
	box-sizing: border-box;
	background-image: url("${(props) =>
		props.$isOrganization ? "/Login_Organization_Desktop_Background.png" : "/Login_Desktop_Background.png"}");
	background-size: cover;
	background-position: center;
	background-repeat: no-repeat;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 2rem 1rem;

	/* Tablet */
	@media (max-width: 1024px) and (min-width: 641px) {
		background-image: url("/Login_Organization_Desktop_Background.png");
	}

	/* Mobile - Keep background but crop nicely */
	@media (max-width: 640px) {
		background-image: url("${(props) =>
			props.$isOrganization ? "/Login_Organization_Desktop_Background.png" : "/Login_Desktop_Background.png"}");
		/* You can create a dedicated mobile background later if needed */

		padding: 1.5rem 1rem;
		background-position: center top; /* Better cropping for mobile */
		background-size: cover;
	}
`;

const AuthCard = styled.div<{ $bankDetails?: boolean }>`
	background: rgba(255, 255, 255, 0.95); /* Slightly transparent white for better blending */
	border-radius: 20px;
	/* Figma (bank details): content starts ~100px from card top, 40px side/bottom insets */
	padding: ${(props) => (props.$bankDetails ? "100px 0.75rem 1.25rem 0.75rem" : "3rem 0.25rem 1rem 0.25rem")};
	box-sizing: border-box;
	box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
	backdrop-filter: blur(12px);
	width: 100%;
	/* Bank details: size to content (≈768px design) so the form never scrolls internally */
	height: ${(props) => (props.$bankDetails ? "auto" : "720px")};
	min-height: ${(props) => (props.$bankDetails ? "768px" : "0")};
	max-width: ${(props) => (props.$bankDetails ? "600px" : "442px")};
	position: relative;
	z-index: 5;
	display: flex;
	flex-direction: column;
	overflow: hidden;

	@media (max-width: 640px) {
		padding: 2rem 1.5rem;
		border-radius: 16px;
		background: rgba(255, 255, 255, 0.97); /* Slightly more opaque on mobile */
	}
`;

const Wordmark = styled(Link)`
	display: block;
	margin: 0 auto 1.75rem;
	width: 300px;
	margin-top: 0.5rem;
	max-width: 100%;

	img {
		display: block;
		width: 100%;
		height: auto;
	}

	@media (max-width: 640px) {
		width: 160px;
		margin-bottom: 1.5rem;
	}
`;

const BackLink = styled(Link)<{ $bankDetails?: boolean }>`
	position: absolute;
	top: ${(props) => (props.$bankDetails ? "54px" : "15px")};
	left: ${(props) => (props.$bankDetails ? "60px" : "20px")};
	display: inline-flex;
	align-items: center;
	gap: ${(props) => (props.$bankDetails ? "12px" : "0.4rem")};
	font-size: ${(props) => (props.$bankDetails ? "14px" : "0.95rem")};
	font-weight: ${(props) => (props.$bankDetails ? 600 : 400)};
	color: ${(props) => (props.$bankDetails ? "#303030" : "#969696ee")};
	text-decoration: none;
	transition: color 0.2s ease;

	&:hover {
		color: #111;
	}

	@media (max-width: 640px) {
		top: ${(props) => (props.$bankDetails ? "24px" : "16px")};
		left: ${(props) => (props.$bankDetails ? "24px" : "16px")};
	}
`;

const AuthContent = styled.div`
	flex: 1 1 auto;
	display: flex;
	flex-direction: column;
	overflow-y: auto;
	overflow-x: hidden;
	min-width: 0;
	padding: 0rem 1.75rem 0rem 1.75rem;
`;

const Main = styled.div`
	flex: 1 1 auto;
	display: flex;
	flex-direction: column;
	min-width: 0;
`;

const Title = styled.h1`
	font-size: 30px;
	font-weight: 600;
	margin-bottom: 0.5rem;
	color: #1a1a1a;
	line-height: 30px;
	text-align: center;

	@media (max-width: 640px) {
		font-size: 1.6rem;
	}
`;

const Subtitle = styled.p`
	font-size: 0.95rem;
	color: #555;
	margin-bottom: 0.25rem;
	text-align: center;
	line-height: 20px;

	@media (max-width: 640px) {
		font-size: 0.9rem;
		margin-bottom: 1.75rem;
	}
`;

interface AuthLayoutProps {
	title?: string | React.ReactNode;
	subtitle: string | React.ReactNode;
	children: React.ReactNode;
	isOrganization?: boolean;
	backLinkTo?: string;
	backLinkLabel?: React.ReactNode;
	onBack?: React.MouseEventHandler<HTMLAnchorElement>;
	bankDetails?: boolean;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({
	title,
	subtitle,
	children,
	isOrganization = false,
	backLinkTo,
	backLinkLabel,
	onBack,
	bankDetails = false,
}) => {
	const location = useLocation();
	const [searchParams] = useSearchParams();

	const rawReturnTo = searchParams.get("returnTo");
	const returnToSafe = isValidInternalPath(rawReturnTo)
		? rawReturnTo
		: isValidInternalPath(backLinkTo)
			? (backLinkTo as string)
			: undefined;

	// Register step flow order
	const steps = [
		"email",
		"verify-code",
		"username",
		"connect-social",
		"tiktok-auth",
		"upload-avatar",
		"set-password",
		"bank-details",
	];

	const currentStep = searchParams.get("step") || "email";
	const idx = steps.indexOf(currentStep);

	// Prefer explicit `backLinkTo` when valid, otherwise use the sanitized `returnToSafe`.
	let effectiveBackTo = isValidInternalPath(backLinkTo) ? (backLinkTo as string) : returnToSafe;

	// If on register route and we have a previous step, back should go to that step (preserve returnTo)
	if (location.pathname === "/register" && idx > 0) {
		const prev = steps[idx - 1];
		const previousStepParams = new URLSearchParams(searchParams);
		previousStepParams.set("step", prev);
		effectiveBackTo = `/register?${previousStepParams.toString()}`;
	}

	// Do not show back navigation on the base login page to avoid confusing UX
	if (location.pathname === "/login") {
		effectiveBackTo = undefined;
	}

	return (
		<AuthContainer $isOrganization={isOrganization}>
			<AuthCard $bankDetails={bankDetails}>
				{effectiveBackTo && (
					<BackLink to={effectiveBackTo} onClick={onBack} $bankDetails={bankDetails}>
						{backLinkLabel || "← Go back"}
					</BackLink>
				)}
				<AuthContent>
					{!bankDetails && (
						<Wordmark to="/" aria-label="Creatorverse home">
							<img src="/Creatorverse_Wordmark_Gradient.svg" alt="Creatorverse" />
						</Wordmark>
					)}
					{title && <Title>{title}</Title>}
					{subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
					<Main>{children}</Main>
				</AuthContent>
				{!bankDetails && <Footer />}
			</AuthCard>
		</AuthContainer>
	);
};

export default AuthLayout;
